import { useEffect, useMemo, useState } from "react";
import { Character, Connection, ConnectionType, Form, FormErrors } from "../../lib/types";
import { X, Link, ArrowLeftRight, ArrowRight } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { capitalize } from "../../lib/helpers";
import { CRIT_COLOR } from "../../lib/constants";
import ToggleBtn from "../interface/ToggleBtn";

interface Props {
  characters: Character[];
  connectionTypes: ConnectionType[];
  onAdd: (connection: Connection) => void;
  onClose: () => void;
}

export default function AddConnectionModal({
  characters,
  connectionTypes,
  onAdd,
  onClose,
}: Props) {
  const [form, setForm] = useState<Omit<Connection, "id"> & { isDirty: boolean, errors?: FormErrors<Connection> }>({ label: "", source: "", target: "", type: "", isDirty: false });
  const [sourceInput, setSourceInput] = useState("");
  const [targetInput, setTargetInput] = useState("");
  const [mutual, setMutual] = useState(true);
  const [dotted, setDotted] = useState(false);

  const sortedCharacters = useMemo(
    () => [...characters].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [characters]
  );

  const sortedTargetCharacters = useMemo(
    () =>
      [...characters]
        .filter((c) => c.id !== form.source)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [characters, form.source]
  );

  useEffect(() => {
    setSourceInput(characters.find((c) => c.id === form.source)?.name ?? "");
  }, [form.source, characters]);

  useEffect(() => {
    setTargetInput(characters.find((c) => c.id === form.target)?.name ?? "");
  }, [form.target, characters]);

  useEffect(() => {
    if (form.source && form.target && form.source === form.target) {
      setForm({ ...form, target: "" });
      setTargetInput("");
    }
  }, [form.source, form.target]);

  const evaluateForm = (f: Form<Connection>) => {
    const errors: FormErrors<Connection> = {};

    if (!f.source?.trim()) {
      errors.source = "Connection must have a source";
    }
    if (characters.filter(c => c.id === f.source).length !== 1) {
      errors.source = "Cannot find character";
    }

    if (!f.target?.trim()) {
      errors.target = "Connection must have a target";
    }
    if (characters.filter(c => c.id === f.target).length !== 1) {
      errors.target = "Cannot find character";
    }

    if (connectionTypes.filter(ct => ct.id === f.type).length !== 1) {
      errors.type = "Cannot find specified type";
    }

    const newForm = { ...f, errors };

    if (Object.keys(errors).length === 0) {
      delete newForm.errors;
    }

    setForm(newForm);
    return newForm;
  };

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();

    const checkedForm = evaluateForm({ ...form, isDirty: true });

    if (checkedForm.errors) return;

    const newConnection: Connection = {
      id: uuidv4(),
      source: checkedForm.source.trim(),
      target: checkedForm.target.trim(),
      type: checkedForm.type.trim(),
      ...(checkedForm.label?.trim() ? { label: checkedForm.label.trim() } : {}),
      unmutual: (mutual ? undefined : true),
      dotted: (dotted ? true : undefined),
    };

    console.log(newConnection);
    onAdd(newConnection)
    onClose()
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "var(--overlay)" }}
    >
      <div
        className="w-full rounded-xl overflow-hidden fade-in"
        style={{
          maxWidth: "38rem",
          background: "var(--panel-gradient)",
          border: "1px solid var(--border-medium)",
          boxShadow: "0 40px 80px var(--shadow-xl)",
        }}
      >
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: "1px solid var(--border-subtle)" }}
        >
          <h3 className="font-display text-lg font-semibold" style={{ color: "var(--text-primary)" }}>
            New Connection
          </h3>
          <button
            onClick={onClose}
            style={{ color: "var(--text-muted)" }}
            className="appearance-none rounded border-0 outline-none bg-transparent hover:bg-white/10 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="cl-label">From *</label>
              <input
                className="cl-input"
                list="characters-from-list"
                value={sourceInput}
                style={{ borderColor: form.isDirty && form.errors?.source ? CRIT_COLOR : "" }}
                title={form.errors?.source || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSourceInput(v);

                  const selected = sortedCharacters.find(
                    (c) => c.name.toLowerCase() === v.toLowerCase()
                  );
                  evaluateForm({ ...form, source: (selected?.id ?? "") });
                }}
                placeholder="Select or type a character…"
              />
              <datalist id="characters-from-list">
                {sortedCharacters.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>

            <div className="flex flex-col items-center gap-1 pb-0.5">
              <span className="cl-label">Relations</span>
              <button
                onClick={() => setMutual(!mutual)}
                className="w-10 h-9 rounded-lg flex items-center justify-center transition-all hover:scale-105"
                style={{
                  background: mutual ? "var(--gold-dim)" : "var(--bg-surface)",
                  border: `1px solid ${mutual ? "var(--gold-border)" : "var(--border-medium)"}`,
                  color: mutual ? "var(--gold)" : "var(--text-muted)",
                }}
              >
                {mutual ? <ArrowLeftRight size={16} /> : <ArrowRight size={16} />}
              </button>
            </div>

            <div className="flex-1">
              <label className="cl-label">To *</label>
              <input
                className="cl-input"
                list="characters-to-list"
                value={targetInput}
                style={{ borderColor: form.isDirty && form.errors?.target ? CRIT_COLOR : "" }}
                title={form.errors?.target || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setTargetInput(v);

                  const selected = sortedTargetCharacters.find(
                    (c) => c.name.toLowerCase() === v.toLowerCase()
                  );
                  evaluateForm({ ...form, target: (selected?.id ?? "") });
                }}
                placeholder="Select or type a character…"
              />
              <datalist id="characters-to-list">
                {sortedTargetCharacters.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>
          </div>

          <div>
            <label className="cl-label mb-2">Type *</label>
            <div
              className="grid grid-cols-2 gap-2"
              title={form.errors?.type || ""}
              style={form.isDirty && form.errors?.type ? { border: `1px solid ${CRIT_COLOR}`, padding: "1ch", borderRadius: 5 } : {}}>
              {connectionTypes.map((t) => (
                <button
                  key={t.id}
                  onClick={() => evaluateForm({ ...form, type: t.id })}
                  className="py-2 px-3 rounded-lg text-sm font-mono text-left flex items-center gap-2 transition-all"
                  style={{
                    background: form.type === t.id ? `${t.color}25` : "var(--bg-surface)",
                    border: form.type === t.id ? `4px solid ${t.color}80` : "1px solid var(--border-subtle)",
                    color: form.type === t.id ? t.color : "var(--text-muted)",
                  }}
                >
                  <span style={{ fontSize: 18 }}>{t.emoji}</span> {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <ToggleBtn label="Dotted" state={dotted} setState={setDotted} />
          </div>

          <div>
            <label className="cl-label">
              Optional label <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({form.label.length}/15)</span>
            </label>
            <input
              className="cl-input"
              placeholder="Married, Nemesis, Cousin…"
              maxLength={15}
              value={form.label}
              onChange={(e) => {
                const v = e.target.value.slice(0, 15);
                evaluateForm({ ...form, label: capitalize(v) });
              }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </div>
        </div>

        <div
          className="px-6 py-4 flex justify-end gap-3"
          style={{ borderTop: "1px solid var(--border-subtle)" }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-mono transition-all hover:scale-105"
            style={{
              color: "var(--text-muted)",
              background: "var(--bg-surface)",
              border: "1px solid var(--border-medium)",
            }}
          >
            Cancel
          </button>

          <button
            onClick={submit}
            disabled={form.isDirty && form.errors !== undefined}
            className="px-5 py-2 rounded-lg text-sm font-mono flex items-center gap-2 transition-all hover:scale-105 disabled:opacity-40"
            style={{
              background: "linear-gradient(135deg, var(--text-muted), var(--gold))",
              color: "var(--bg-deep)",
              border: "1px solid var(--gold-border)",
              fontWeight: 600,
            }}
          >
            <Link size={14} /> Connect
          </button>
        </div>
      </div >
    </div >
  );
}
